import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../context/AppContext";
import useHttp from "../../../hooks/useHttp";
import { parseFetchDataEnvelope } from "../utils/workflowApiResponse";
import type { NormalizedStepConfig } from "../types/workflowSharedTypes";
import { normalizeStepConfig } from "../utils/normalizeStepConfig";
import { mergeCrmActionEnumPatches, readWorkflowActionMetaConfigs } from "../utils/workflowCrmActionOverrides";

export interface WorkflowEnumItem {
    code: string;
    idleLabel: string;
    activeLabel: string;
    order: number;
    meta: {
        group?: string;
        groupLabel?: string;
        kind?: string;
        description?: string;
        message?: string;
        config?: Record<string, unknown>;
        supportedTriggers?: string[];
        configVersion?: number;
        formFields?: unknown[];
        customViewKey?: string;
        customViewParams?: Record<string, unknown>;
        logInCrmNodeSummaryMode?: "simple" | "extended";
    };
    stepConfig?: NormalizedStepConfig;
}

export interface WorkflowEnumOption {
    value: string;
    label: string;
    desc?: string;
}

export interface WorkflowEnumsData {
    triggerTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    conditionTypes: WorkflowEnumItem[];
    scheduleOptions?: WorkflowEnumOption[];
}

export interface UseWorkflowEnumsResult {
    conditionTypes: WorkflowEnumItem[];
    actionTypes: WorkflowEnumItem[];
    triggerTypes: WorkflowEnumItem[];
    scheduleOptions: WorkflowEnumOption[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

const DEFAULT_ENUMS: WorkflowEnumsData = {
    triggerTypes: [],
    actionTypes: [],
    conditionTypes: [],
    scheduleOptions: [],
};

interface WorkflowEnumsApiData {
    triggerTypes?: WorkflowEnumItem[];
    actionTypes?: WorkflowEnumItem[];
    conditionTypes?: WorkflowEnumItem[];
}

export function useWorkflowEnums(): UseWorkflowEnumsResult {
    const [appProperties] = useContext(AppContext);
    const { fetchData } = useHttp();
    const appPropertiesRef = useRef(appProperties);
    appPropertiesRef.current = appProperties;

    const [data, setData] = useState<WorkflowEnumsData>(DEFAULT_ENUMS);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const loadEnums = useCallback(async () => {
        const ap = appPropertiesRef.current;
        if (!ap?.companyId || !ap?.userId) return;
        setIsLoading(true);
        setError(null);
        try {
            const raw = await fetchData("workflows/metadata", "GET", null, ap);
            if (raw == null || raw === "") {
                setData(DEFAULT_ENUMS);
                return;
            }
            const { entity } = parseFetchDataEnvelope(raw);
            if (!entity || typeof entity !== "object" || Array.isArray(entity)) {
                setData(DEFAULT_ENUMS);
                return;
            }
            const p = entity as WorkflowEnumsApiData;
            const conditionTypes = p.conditionTypes ?? [];
            const runScheduleConditionItem = conditionTypes.find((c) => c.code === "Condition.Time.RunSchedule");
            const scheduleConfigFromApi = runScheduleConditionItem?.meta?.config?.schedule as
                | Array<{ code?: string; idleLabel?: string; meta?: { description?: string } }>
                | undefined;
            const scheduleOptionsFromApi: WorkflowEnumOption[] = Array.isArray(scheduleConfigFromApi)
                ? scheduleConfigFromApi.map((scheduleItem) => {
                      const scheduleCode = scheduleItem.code ?? "";
                      const scheduleValue = scheduleCode === "Custom" ? "SpecificHours" : scheduleCode;
                      const scheduleLabel =
                          scheduleCode === "Custom"
                              ? "Specific Hours"
                              : (scheduleItem.idleLabel ?? scheduleItem.code ?? "");
                      return { value: scheduleValue, label: scheduleLabel, desc: scheduleItem.meta?.description };
                  })
                : [];

            const mapWithStepConfig = (items: WorkflowEnumItem[]): WorkflowEnumItem[] =>
                items.map((item) => ({
                    ...item,
                    stepConfig: normalizeStepConfig(item.meta as Record<string, unknown>, item.code),
                }));

            const crmPatches = readWorkflowActionMetaConfigs(ap?.controller);
            const actionTypesMerged = mergeCrmActionEnumPatches(p.actionTypes ?? [], crmPatches);

            setData({
                triggerTypes: mapWithStepConfig(p.triggerTypes ?? []),
                actionTypes: mapWithStepConfig(actionTypesMerged),
                conditionTypes: mapWithStepConfig(conditionTypes),
                scheduleOptions: scheduleOptionsFromApi.length > 0 ? scheduleOptionsFromApi : undefined,
            });
        } catch (e) {
            setError(e instanceof Error ? e : new Error(String(e)));
            setData(DEFAULT_ENUMS);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!appProperties?.currentUser?.osyncUserId) {
            setIsLoading(false);
            return;
        }
        loadEnums();
    }, [appProperties?.currentUser?.osyncUserId, appProperties?.controller?.service]);

    const scheduleOptions =
        data.scheduleOptions && data.scheduleOptions.length > 0
            ? data.scheduleOptions
            : [
                  { value: "BusinessHours", label: "Business Hours", desc: "Run during your business hours" },
                  { value: "OutsideBusinessHours", label: "Outside Business Hours", desc: "Run outside your business hours " },
                  { value: "SpecificHours", label: "Specific Hours", desc: "Run during a custom time range" },
              ];

    return {
        conditionTypes: data.conditionTypes,
        actionTypes: data.actionTypes,
        triggerTypes: data.triggerTypes,
        scheduleOptions,
        isLoading,
        error,
        refetch: loadEnums,
    };
}
